import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { HeaderNav } from '../components/HeaderNav';
import { Footer } from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { AspectRatio } from '../components/ui/aspect-ratio';
import { Button } from '../components/ui/button';
import { Share2, Instagram, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function VideoPage() {
  const { slug } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/videos/${slug}`);
        setVideo(response.data);
        
        // Track view
        if (response.data?.id) {
          axios.post(`${API_URL}/api/videos/${response.data.id}/view`).catch(() => {});
        }
      } catch (error) {
        console.error('Error fetching video:', error);
        toast.error('Video not found');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchVideo();
    }
  }, [slug]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: video?.title,
        text: video?.dek,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <>
        <HeaderNav />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading video...</p>
          </div>
        </div>
      </>
    );
  }

  if (!video) {
    return (
      <>
        <HeaderNav />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold mb-2">Video not found</h2>
            <Link to="/" className="text-primary hover:underline">Return to home</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const currentUrl = window.location.href;
  const ogImage = video.thumbnail || 'https://via.placeholder.com/1200x630?text=B%26B+Magazine+Video';
  const ogDescription = video.dek || 'Watch this video on B&B Magazine';

  return (
    <>
      <Helmet>
        <title>{video.title} | B&B Magazine</title>
        <meta name="description" content={ogDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="video.other" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={video.title} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="B&B Magazine" />
        {video.publishedAt && <meta property="video:release_date" content={video.publishedAt} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="player" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={video.title} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <HeaderNav />

      <main>
        <article>
          {/* Video Content */}
          <div className="container max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Series Badge */}
            {video.series && (
              <Badge className="mb-4 px-2.5 py-1 rounded-full text-xs uppercase tracking-wide bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))] border border-[hsl(var(--primary)/0.3)]">
                {video.series.replace('-', ' ')}
              </Badge>
            )}

            {/* Title */}
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-[1.05] mb-5">
              {video.title}
            </h1>

            {/* Dek */}
            {video.dek && (
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-6">
                {video.dek}
              </p>
            )}

            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
              <span className="font-ui">{video.author || 'B&B Editorial'}</span>
              {video.publishedAt && (
                <>
                  <span>•</span>
                  <span>{formatDate(video.publishedAt)}</span>
                </>
              )}
              <button 
                onClick={handleShare}
                className="ml-auto flex items-center gap-2 text-primary hover:text-primary/80 transition-colors duration-200"
              >
                <Share2 className="h-4 w-4" />
                Share
              </button>
            </div>

            <Separator className="mb-8" />

            {/* Video Player / Instagram Reel */}
            <div className="mb-8">
              {video.platform === 'instagram' ? (
                <AspectRatio ratio={9/16} className="max-w-md mx-auto">
                  <a
                    href={video.embedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative block w-full h-full rounded-lg overflow-hidden border border-border hover:border-primary transition-colors duration-200"
                  >
                    <img
                      src={video.thumbnailUrl}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/20 group-hover:bg-black/30 transition-colors duration-200">
                      <Instagram className="h-16 w-16 text-white drop-shadow-lg" />
                      <Button className="gap-2">
                        View on Instagram
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </a>
                </AspectRatio>
              ) : (
                <AspectRatio ratio={16/9}>
                  <iframe
                    src={video.embedUrl}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full rounded-lg border border-border"
                  />
                </AspectRatio>
              )}
            </div>

            {/* Tags */}
            {video.tags && video.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-border">
                <div className="flex flex-wrap gap-2">
                  {video.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="font-ui">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}